import { useRef } from 'react'
import { IconCheck, IconRepeat } from './Icons'
import { arm, disarm, listMoving, movedSince, tapFirst, useArmed } from '../lib/checkGuard'

/**
 * Task checkbox. The real input is an iOS `switch` checkbox stretched over the target:
 * iOS 26+ only produces haptics for real taps on such controls (SPEC §8.1).
 *
 * `guard`: a box in a list (its row's key). On a phone, a touch that stops the list sliding
 * doesn't check it, and with "Tap a task first" on, the first tap makes its task ready and the
 * second checks it — unless it's `ready` already (the row being typed in). lib/checkGuard.ts.
 */
export function Checkbox({
  checked,
  onToggle,
  label,
  recurring,
  size = 'md',
  guard,
  ready,
}: {
  checked: boolean
  onToggle: () => void
  label?: string
  recurring?: boolean
  size?: 'md' | 'sm'
  guard?: string
  ready?: boolean
}) {
  const switchAttr = { switch: '' } as Record<string, string>
  const armed = useArmed(guard)
  const down = useRef<{ at: number; touch: boolean; moving: boolean } | null>(null)
  return (
    <label
      className={`cb ${checked ? 'checked' : ''} ${armed ? 'armed' : ''} ${size === 'sm' ? 'scale-90' : ''}`}
      data-arm={guard}
      onPointerDown={(e) => {
        e.stopPropagation()
        down.current = { at: performance.now(), touch: e.pointerType !== 'mouse', moving: listMoving() }
      }}
      onTouchStart={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        {...switchAttr}
        checked={checked}
        onChange={() => {
          const d = down.current && performance.now() - down.current.at < 2000 ? down.current : null
          down.current = null
          if (guard && d?.touch) {
            // The thumb that stopped (or moved) the list, not a tap on the box.
            if (d.moving || movedSince(d.at)) return
            if (!ready && !armed && tapFirst()) return arm(guard)
          }
          if (armed) disarm()
          onToggle()
        }}
        aria-label={label ? `${checked ? 'Uncheck' : 'Check'} ${label}` : checked ? 'Uncheck' : 'Check'}
      />
      <span className="box" aria-hidden="true">
        {checked ? <IconCheck size={12} /> : recurring ? <IconRepeat size={10} className="text-faint" /> : null}
      </span>
    </label>
  )
}
